import { useCallback, useEffect, useRef } from "react";
import { useServiceCenter } from "../contexts";
import { processWebSocketMessage } from "../utils/messageProcessor";
import { normalizePhone } from "../utils/formatters";

/**
 * Hook para gerenciar WebSocket e mensagens em tempo real
 */
const useWebSocket = (onNewMessage, onMessageStatusUpdate) => {
  const {
    accounts,
    selectedAccount,
    selectedConversation,
    incrementUnreadCount,
    updateConversation,
    conversations,
  } = useServiceCenter();

  const handlersRef = useRef({ onNewMessage, onMessageStatusUpdate });

  // Atualizar refs quando handlers mudarem
  useEffect(() => {
    handlersRef.current = { onNewMessage, onMessageStatusUpdate };
  }, [onNewMessage, onMessageStatusUpdate]);

  /**
   * Handler para novas mensagens
   */
  const handleNewMessage = useCallback(
    (event) => {
      try {
        if (!event?.data?.newWaMessage && !event?.data?.newWaSentMessage) {
          return;
        }

        const newMessage =
          event.data.newWaMessage || event.data.newWaSentMessage;

        // Multi-canal: o backend agora envia `channel` no payload.
        // Para Instagram, comparações usam ig_business_account_id em vez
        // de telefone — mas IGSID é numérico, então normalizePhone ainda
        // produz um valor consistente para match.
        const messageChannel = newMessage.channel || 'whatsapp';

        // Identifica a conta da mensagem (from_me => from; senão => to)
        const ourIdentifier = normalizePhone(
          newMessage.from_me ? newMessage.from : newMessage.to
        );

        // Confere se a conta é conhecida. Para Instagram, compara contra
        // ig_business_account_id; para WhatsApp, contra phone.
        const isKnownAccount = (accounts || []).some((acc) => {
          if (messageChannel === 'instagram') {
            return (
              normalizePhone(acc.ig_business_account_id) === ourIdentifier ||
              normalizePhone(acc.phone) === ourIdentifier
            );
          }
          return normalizePhone(acc.phone) === ourIdentifier;
        });
        if (!isKnownAccount) return;
        const ourPhone = ourIdentifier;

        // Modo omnichannel: a lista de conversas vem de TODAS as accounts
        // ativas mescladas. Não filtra mais por "selectedAccount" — qualquer
        // mensagem de uma conta nossa é processada.

        // Validar conteúdo
        const messageText = newMessage.message || "";
        const hasMedia = newMessage.has_media || false;
        if (!messageText.trim() && !hasMedia) return;

        // Phone do contato do outro lado da conversa
        const chatPhone = newMessage.from_me
          ? normalizePhone(newMessage.to)
          : normalizePhone(newMessage.from);

        // Unread count: pra qualquer mensagem entrante. Se a conversa
        // está aberta no chat, NÃO incrementa.
        if (!newMessage.from_me) {
          const isOpenChat =
            normalizePhone(selectedConversation?.phone) === chatPhone;
          if (!isOpenChat) {
            incrementUnreadCount(chatPhone);
          }
        }

        const processedMessages = processWebSocketMessage(newMessage);
        if (!processedMessages || processedMessages.length === 0) return;

        // Se é a conversa aberta no chat, dispara handler de nova msg
        if (normalizePhone(selectedConversation?.phone) === chatPhone) {
          handlersRef.current.onNewMessage?.(processedMessages);
        }

        // Atualizar preview na lista de conversas
        const firstMessage = processedMessages[0];
        const existingConv = conversations.find(
          (conv) => normalizePhone(conv.phone) === chatPhone
        );

        if (existingConv) {
          // Se a msg é mídia sem texto, mostra rótulo no preview em vez de "undefined..."
          const previewSource = firstMessage.text || "";
          const previewLabel = previewSource
            ? previewSource.length > 30
              ? `${previewSource.substring(0, 30)}...`
              : previewSource
            : firstMessage.has_media
            ? "📎 Mídia"
            : "";

          updateConversation({
            ...existingConv,
            lastMessage: previewLabel,
            lastMessageTime: firstMessage.time,
            lastMessageTimestamp: firstMessage.timestamp,
          });
        }
      } catch (error) {
        console.error("Erro ao processar mensagem WebSocket:", error);
      }
    },
    [
      accounts,
      selectedAccount?.phone,
      selectedConversation?.phone,
      conversations,
      incrementUnreadCount,
      updateConversation,
    ]
  );

  /**
   * Handler para atualização de status de mensagem
   */
  const handleMessageStatusUpdate = useCallback((event) => {
    try {
      // Success message
      if (event?.data?.waMessageSuccess) {
        const { message_id, status } = event.data.waMessageSuccess;

        if (handlersRef.current.onMessageStatusUpdate) {
          handlersRef.current.onMessageStatusUpdate(
            message_id,
            status === 200 ? "delivered" : "error",
            status !== 200 ? event.data.message?.message : null
          );
        }
      }

      // Error message
      if (event?.data?.waMessageError) {
        const { message: errorMessage, message_id } = event.data.waMessageError;

        if (handlersRef.current.onMessageStatusUpdate) {
          handlersRef.current.onMessageStatusUpdate(
            message_id,
            "error",
            errorMessage
          );
        }
      }
    } catch (error) {
      console.error("Erro ao processar atualização de status:", error);
    }
  }, []);

  // Registrar listeners de WebSocket
  useEffect(() => {
    window.addEventListener("message", handleNewMessage);
    window.addEventListener("message", handleMessageStatusUpdate);

    return () => {
      window.removeEventListener("message", handleNewMessage);
      window.removeEventListener("message", handleMessageStatusUpdate);
    };
  }, [handleNewMessage, handleMessageStatusUpdate]);

  return {
    // Expor handlers para uso externo se necessário
    handleNewMessage,
    handleMessageStatusUpdate,
  };
};

export default useWebSocket;
